import React, { useState } from 'react';
import { toast } from 'react-hot-toast';
import { GamePlan, Team } from '../../types';
import { useAIService } from '../../services/aiService.simplified';
import Modal from '../Modal';
import { supabase } from '../../lib/supabase';

type GamePlanWithAnalysis = GamePlan;

interface GamePlanRegenerateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  gamePlan: GamePlan;
}

const regenerationAreas = [
  { id: 'offense', label: 'Offensive Strategy' },
  { id: 'defense', label: 'Defensive Strategy' }
];

const detailLevels = [
  { value: 'basic', label: 'Basic' },
  { value: 'detailed', label: 'Detailed' },
  { value: 'advanced', label: 'Advanced' }
];

const GamePlanRegenerateModal: React.FC<GamePlanRegenerateModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  gamePlan
}) => {
  const [selectedAreas, setSelectedAreas] = useState<string[]>(['offense', 'defense']);
  const [detailLevel, setDetailLevel] = useState<'basic' | 'detailed' | 'advanced'>('detailed');
  const [customInstructions, setCustomInstructions] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { generateGamePlan } = useAIService();

  const handleAreaToggle = (areaId: string) => {
    setSelectedAreas(prev => 
      prev.includes(areaId)
        ? prev.filter(id => id !== areaId)
        : [...prev, areaId]
    );
  };

  const buildAIPrompt = () => {
    const prompt = `
      Generate a ${detailLevel} game plan strategy based on the following information:

      Selected Areas:
      ${selectedAreas.join(', ')}

      Current Game Plan:
      ${JSON.stringify(gamePlan, null, 2)}
    `;
    return prompt;
  };

  const handleRegenerate = async () => {
    try {
    setIsLoading(true);
      const prompt = buildAIPrompt();
      console.log('AI Prompt:', prompt);

      const aiResponse = await generateGamePlan(prompt);
      console.log('AI Response:', aiResponse);

      // Update the game plan with the new strategies
      const updateData = {
        offensive_strategy: aiResponse.offenseStrategy,
        defensive_strategy: aiResponse.defenseStrategy,
        updated_at: new Date().toISOString()
      };

      const { error } = await supabase
        .from('game_plans')
        .update(updateData)
        .eq('id', gamePlan.id);

      if (error) throw error;

      toast.success('Game plan regenerated successfully');
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error regenerating game plan:', error);
      toast.error('Failed to regenerate game plan');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Regenerate Game Plan"
    >
      <div className="space-y-6">
        {/* Regeneration Areas */}
        <div>
          <h3 className="text-lg font-medium mb-3">Select Areas to Regenerate</h3>
          <div className="space-y-2">
            {regenerationAreas.map((area) => (
              <label key={area.id} className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={selectedAreas.includes(area.id)}
                  onChange={() => handleAreaToggle(area.id)}
                  className="rounded border-gray-300"
                />
                <span>{area.label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Detail Level */}
        <div>
          <h3 className="text-lg font-medium mb-3">Detail Level</h3>
          <div className="flex space-x-2">
            {detailLevels.map((level) => (
              <button
                key={level.value}
                onClick={() => setDetailLevel(level.value as 'basic' | 'detailed' | 'advanced')}
                className={`
                  px-4 py-2 rounded ${
                  detailLevel === level.value
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 hover:bg-gray-200'
                  }
                `}
              >
                {level.label}
              </button>
            ))}
          </div>
        </div>

        {/* Custom Instructions */}
        <div>
          <h3 className="text-lg font-medium mb-3">Custom Instructions (Optional)</h3>
          <textarea
            value={customInstructions}
            onChange={(e) => setCustomInstructions(e.target.value)}
            placeholder="Enter any specific instructions or focus areas..."
            className="w-full h-32 p-3 border rounded-lg"
          />
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 border rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleRegenerate}
            disabled={isLoading || selectedAreas.length === 0}
            className={`px-4 py-2 rounded-lg ${
              isLoading || selectedAreas.length === 0
                ? 'bg-gray-300 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            {isLoading ? 'Regenerating...' : 'Regenerate'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default GamePlanRegenerateModal; 